import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2, Bookmark, Zap, Activity, Target, Check, X } from 'lucide-react';
import { Button } from './button';
import ReactDOM from 'react-dom';
import { useNoshStore } from '../../lib/store';
import { useLeftEdgeSwipeDisable } from '../../hooks/useLeftEdgeSwipeDisable';
import { cancelMealScan, getImageDisplayUrl } from '../../lib/api';
import { CachedImage } from './CachedImage';
import { formatCalories, formatMacronutrient, formatRemainingCalories } from '../../lib/utils';

interface NutritionData {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  servingSize: string;
  mealName: string;
  ingredients: string[];
  confidence: number;
  ai_suggestions?: string[];
}

const GlobalNutritionAnalysisModal = () => {
  const { 
    nutritionAnalysis, 
    clearNutritionAnalysis,
    setNutritionAnalysisError,
    addMeal 
  } = useNoshStore();

  // Prevent left edge swipe on modal
  useLeftEdgeSwipeDisable({
    pageId: 'NutritionAnalysisModal',
    enabled: nutritionAnalysis.showModal,
    edgeWidth: 50
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false); // Track if meal was auto-saved
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(null);
  const [mealImage, setMealImage] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(true);
  const [hasRealData, setHasRealData] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isPWA, setIsPWA] = useState(false);
  
  // Add hasAnimated state for performance optimization
  const [hasAnimated, setHasAnimated] = useState(false);

  const isOpen = nutritionAnalysis.showModal;

  // Check for PWA mode with session-based logging to prevent duplicate logs
  useEffect(() => {
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          (window.navigator as any).standalone ||
                          document.referrer.includes('android-app://');
      setIsPWA(isStandalone);
      
      // Only log PWA detection once per session to prevent spam
      const sessionKey = 'pwa_detection_logged';
      if (!sessionStorage.getItem(sessionKey)) {
        // PWA detection for modal layout
        sessionStorage.setItem(sessionKey, 'true');
      }
    };
    
    checkPWA();
  }, []);

  // Mock user profile for daily progress
  const userProfile = {
    dailyCalorieGoal: 2100,
    currentIntake: 1240
  };

  // Handle resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load meal data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadMealData = async () => {
      try {
        setIsLoading(true);
        
        if (nutritionAnalysis.mealData) {
          // Processing meal data
          
          // Convert AI response to our interface format
          const convertedData: NutritionData = {
            calories: nutritionAnalysis.mealData.estimated_calories || nutritionAnalysis.mealData.calories || 385,
            protein: nutritionAnalysis.mealData.macros?.protein || nutritionAnalysis.mealData.protein || 28,
            carbs: nutritionAnalysis.mealData.macros?.carbs || nutritionAnalysis.mealData.carbs || 18,
            fat: nutritionAnalysis.mealData.macros?.fat || nutritionAnalysis.mealData.fat || 24,
            fiber: nutritionAnalysis.mealData.nutrition?.fiber || nutritionAnalysis.mealData.fiber || 12,
            sugar: nutritionAnalysis.mealData.nutrition?.sugar || nutritionAnalysis.mealData.sugar || 8,
            sodium: nutritionAnalysis.mealData.nutrition?.sodium || nutritionAnalysis.mealData.sodium || 420,
            servingSize: nutritionAnalysis.mealData.serving_size || "1 bowl (280g)",
            mealName: nutritionAnalysis.mealData.meal_name || "AI Analyzed Meal",
            ingredients: nutritionAnalysis.mealData.ingredients || ["Various ingredients"],
            confidence: nutritionAnalysis.mealData.confidence || 85,
            ai_suggestions: nutritionAnalysis.mealData.ai_suggestions || []
          };
          
          setNutritionData(convertedData);
          
          // Handle image loading
          if (nutritionAnalysis.imageUrl) {
            await loadMealImage(nutritionAnalysis.imageUrl);
          } else {
            setMealImage('/food.jpg');
            setImageLoading(false);
          }
          
          setHasRealData(true);
          // Nutrition data loaded successfully
          
          // 🚀 AUTO-SAVE: Only auto-save for NEW photo scans (analysis mode), NOT for viewing saved meals
          if (nutritionAnalysis.mode === 'analysis') {
            await autoSaveMeal(convertedData);
          }
        } else {
          console.log('⚠️ [Global Modal] No meal data available');
          setMealImage('/food.jpg');
          setImageLoading(false);
        }
      } catch (error) {
        console.error('❌ [Global Modal] Error loading meal data:', error);
        setNutritionAnalysisError(error instanceof Error ? error.message : 'Failed to load meal data');
        setMealImage('/food.jpg');
        setImageLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadMealData();
  }, [isOpen, nutritionAnalysis.mealData, nutritionAnalysis.imageUrl]);

  // Helper function to load meal image
  const loadMealImage = async (imageUrl: string) => {
    try {
      setImageLoading(true);
      console.log('🖼️ [Global Modal] Loading image from URL:', imageUrl);
      
      // Check if it's already a valid signed URL
      if (imageUrl.includes('supabase') && imageUrl.includes('token=')) {
        try {
          const urlParams = new URL(imageUrl).searchParams;
          const expiresAt = urlParams.get('expires');
          const isExpired = expiresAt ? new Date(parseInt(expiresAt) * 1000) < new Date() : false;
          
          if (!isExpired) {
            console.log('🔗 [Global Modal] Using existing valid signed URL');
            setMealImage(imageUrl);
            return;
          } else {
            console.log('⏰ [Global Modal] Signed URL expired, need to regenerate');
          }
        } catch (urlError) {
          console.log('⚠️ [Global Modal] Invalid URL format, treating as path');
        }
      }
      
      // If this looks like a storage path or expired URL, generate fresh signed URL
      if (imageUrl.includes('images/') || imageUrl.includes('supabase')) {
        let pathToUse = imageUrl;
        
        // Extract path from signed URL if needed
        if (imageUrl.includes('/object/sign/images/')) {
          pathToUse = imageUrl.split('/object/sign/images/')[1]?.split('?')[0] || imageUrl;
          console.log('📂 [Global Modal] Extracted path from signed URL:', pathToUse);
        }
        
        // Generate fresh signed URL
        const displayUrl = await getImageDisplayUrl(pathToUse, 86400);
        setMealImage(displayUrl);
        console.log('✅ [Global Modal] Generated fresh signed URL for display');
      } else {
        // Direct URL or external image
        console.log('🔗 [Global Modal] Using direct image URL');
        setMealImage(imageUrl);
      }
    } catch (error) {
      console.error('❌ [Global Modal] Image loading failed:', error);
      console.log('🔄 [Global Modal] Falling back to default image');
      setMealImage('/food.jpg');
    } finally {
      setImageLoading(false);
    }
  };

  // 🚀 AUTO-SAVE: Automatically save meal after analysis completes
  const autoSaveMeal = async (nutritionData: NutritionData) => {
    if (isSaved || isSaving) return; // Prevent duplicate saves

    try {
      setIsSaving(true);
      console.log('🚀 [Global Modal] Auto-saving meal to database...');

      // Use the permanent image path if available, otherwise use the current display URL
      const imageUrlToSave = nutritionAnalysis.imagePath || mealImage || '/food.jpg';
      console.log('💾 [Global Modal] Auto-saving meal with image URL:', imageUrlToSave);
      
      await addMeal({
        mealName: nutritionData.mealName,
        imageUrl: imageUrlToSave,
        servingSize: nutritionData.servingSize,
        confidence: nutritionData.confidence,
        calories: nutritionData.calories,
        protein: nutritionData.protein,
        carbs: nutritionData.carbs,
        fat: nutritionData.fat,
        fiber: nutritionData.fiber,
        sugar: nutritionData.sugar,
        sodium: nutritionData.sodium,
        ingredients: nutritionData.ingredients,
        aiSuggestions: nutritionData.ai_suggestions || []
      });

      setIsSaved(true);
      console.log('✅ [Global Modal] Meal auto-saved successfully and logged to nutrition tracking');
    } catch (error) {
      console.error('❌ [Global Modal] Failed to auto-save meal:', error);
      setNutritionAnalysisError(error instanceof Error ? error.message : 'Failed to save meal');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMeal = async () => {
    if (!nutritionData || isSaving) return;

    try {
      setIsSaving(true);
      console.log('💾 [Global Modal] Saving meal to database...');

      // Use the permanent image path if available, otherwise use the current display URL
      const imageUrlToSave = nutritionAnalysis.imagePath || mealImage || '/food.jpg';
      console.log('💾 [Global Modal] Saving meal with image URL:', imageUrlToSave);
      
      await addMeal({
        mealName: nutritionData.mealName,
        imageUrl: imageUrlToSave,
        servingSize: nutritionData.servingSize,
        confidence: nutritionData.confidence,
        calories: nutritionData.calories,
        protein: nutritionData.protein,
        carbs: nutritionData.carbs,
        fat: nutritionData.fat,
        fiber: nutritionData.fiber,
        sugar: nutritionData.sugar,
        sodium: nutritionData.sodium,
        ingredients: nutritionData.ingredients,
        aiSuggestions: nutritionData.ai_suggestions || []
      });

      console.log('✅ [Global Modal] Meal saved successfully');
      handleClose();
    } catch (error) {
      console.error('❌ [Global Modal] Failed to save meal:', error);
      setNutritionAnalysisError(error instanceof Error ? error.message : 'Failed to save meal');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    clearNutritionAnalysis();
    setNutritionData(null);
    setMealImage('');
    setImageLoading(true);
    setHasRealData(false);
    setIsLoading(true);
    setIsSaving(false);
    setIsSaved(false);
  };

  // Reset hasAnimated when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasAnimated(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Desktop backdrop - only show on desktop */}
      {!isMobile && (
        <motion.div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={hasAnimated ? { duration: 0 } : { duration: 0.1, ease: "linear" }}
        />
      )}
      
      {/* Container */}
      <div className={`relative z-10 ${
        isMobile 
          ? 'h-full' // Mobile: full height
          : 'flex items-center justify-center min-h-screen p-4' // Desktop: centered
      }`}>
        <motion.div
          className={`w-full overflow-hidden ${
            isMobile 
              ? 'h-full bg-white dark:bg-gray-800' // Mobile: full screen, no rounded corners
              : 'bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl mx-4 max-h-[90vh]' // Desktop: contained modal
          }`}
          initial={{ 
            opacity: 0, 
            scale: isMobile ? 1 : 0.95, 
            y: isMobile ? 0 : 20 // Mobile: no movement, Desktop: slight upward
          }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0 
          }}
          exit={{ 
            opacity: 0, 
            scale: isMobile ? 1 : 0.95, 
            y: isMobile ? 0 : 20 // Mobile: no movement, Desktop: slight upward
          }}
          transition={hasAnimated ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
          onAnimationComplete={() => setHasAnimated(true)}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Nutrition Analysis
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isLoading ? 'Loading analysis...' : 'AI-powered meal analysis'}
                </p>
              </div>
            </div>
            {/* X button - Both Mobile and Desktop */}
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Content */}
          <div className={`flex-1 overflow-y-auto ${isMobile ? 'h-full' : 'max-h-[calc(90vh-180px)]'}`}>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Analyzing your meal...</p>
                </div>
              </div>
            ) : nutritionData ? (
              <div className={`${isMobile ? 'space-y-4 p-4 pb-40' : 'grid grid-cols-2 gap-6 p-6'}`}>
                {/* Left Column - Taller Square Image */}
                <div className="space-y-4">
                  {/* Meal Image - Made taller and more square */}
                  <div className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700" style={{ aspectRatio: isMobile ? '1' : '0.9' }}>
                    {imageLoading ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <CachedImage
                        src={mealImage}
                        alt={nutritionData.mealName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        fallbackSrc="/food.jpg"
                      />
                    )}
                    
                    {/* Confidence Badge - only show for new photo scans (analysis mode), not saved meals from recipes */}
                    {nutritionAnalysis.mode === 'analysis' && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-lg text-sm font-medium">
                        {nutritionData.confidence}% confident
                      </div>
                    )}
                  </div>

                  {/* Mobile: Show meal info and daily progress below image */}
                  {isMobile && (
                    <>
                      {/* Meal Info */}
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                          {nutritionData.mealName}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                          Serving size: {nutritionData.servingSize}
                        </p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-300">Total Calories</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                              {formatCalories(nutritionData.calories)}
                            </p>
                          </div>
                          <Zap className="text-orange-500" size={28} />
                        </div>
                      </div>

                      {/* Auto-Save Status - Mobile */}
                      <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                        {isSaving ? (
                          <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            <span className="font-medium">Saving meal to tracker...</span>
                          </div>
                        ) : isSaved ? (
                          <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                            <Check size={20} />
                            <span className="font-medium">✅ Meal saved and logged to nutrition tracking!</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            <span className="font-medium">Analyzing meal...</span>
                          </div>
                        )}
                      </div>

                      {/* Daily Progress */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">Daily Calorie Intake</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatRemainingCalories(userProfile.dailyCalorieGoal - (userProfile.currentIntake + formatCalories(nutritionData.calories)))}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-300">Current: {userProfile.currentIntake} cal</span>
                            <span className="text-gray-600 dark:text-gray-300">Goal: {userProfile.dailyCalorieGoal} cal</span>
                          </div>
                          
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 relative overflow-hidden">
                            {/* Current intake (blue) - Top layer, grows first */}
                            <motion.div
                              className="bg-blue-400 h-full rounded-full relative z-10"
                              initial={{ width: "0%" }}
                              animate={{ width: `${Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                              transition={{ duration: 1, ease: "easeOut" }}
                            />
                            {/* Total intake (orange) - Background layer, starts halfway through */}
                            <motion.div
                              className="bg-orange-400 h-full rounded-full absolute top-0 left-0"
                              initial={{ width: "0%" }}
                              animate={{ width: `${Math.min(((userProfile.currentIntake + formatCalories(nutritionData.calories)) / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                              transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                            />
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3 text-xs">
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                <span className="text-gray-600 dark:text-gray-300">Current</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                <span className="text-gray-600 dark:text-gray-300">+ This meal</span>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {Math.round(((userProfile.currentIntake + formatCalories(nutritionData.calories)) / userProfile.dailyCalorieGoal) * 100)}% of goal
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Desktop-only: Ingredients below image */}
                  {!isMobile && nutritionData.ingredients.length > 0 && (
                    <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Detected Ingredients</h3>
                      <div className="flex flex-wrap gap-2">
                        {nutritionData.ingredients.map((ingredient, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Meal Info and Daily Progress Stacked */}
                <div className="space-y-4">
                  {/* Desktop-only: Meal Info at top of right column */}
                  {!isMobile && (
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-3">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {nutritionData.mealName}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                        Serving size: {nutritionData.servingSize}
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-300">Total Calories</p>
                          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                            {formatCalories(nutritionData.calories)}
                          </p>
                        </div>
                        <Zap className="text-orange-500" size={24} />
                      </div>
                    </div>
                  )}

                  {/* Desktop-only: Daily Progress below meal info */}
                  {!isMobile && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-semibold text-gray-700 dark:text-gray-300">Daily Calorie Intake</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatRemainingCalories(userProfile.dailyCalorieGoal - (userProfile.currentIntake + formatCalories(nutritionData.calories)))}
                        </span>
                      </div>
                      
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 dark:text-gray-300">Current: {userProfile.currentIntake} cal</span>
                          <span className="text-gray-600 dark:text-gray-300">Goal: {userProfile.dailyCalorieGoal} cal</span>
                        </div>
                        
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 relative overflow-hidden">
                          {/* Current intake (blue) - Top layer, grows first */}
                          <motion.div
                            className="bg-blue-400 h-full rounded-full relative z-10"
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                          />
                          {/* Total intake (orange) - Background layer, starts halfway through */}
                                                      <motion.div
                              className="bg-orange-400 h-full rounded-full absolute top-0 left-0"
                              initial={{ width: "0%" }}
                              animate={{ width: `${Math.min(((userProfile.currentIntake + formatCalories(nutritionData.calories)) / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                              transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                            />
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                              <span className="text-gray-600 dark:text-gray-300">Current</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                              <span className="text-gray-600 dark:text-gray-300">+ This meal</span>
                            </div>
                          </div>
                          <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                            {Math.round(((userProfile.currentIntake + nutritionData.calories) / userProfile.dailyCalorieGoal) * 100)}% of goal
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Macros */}
                  <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Macronutrients</h3>
                    <div className="space-y-2">
                      {[
                        { name: 'Protein', value: formatMacronutrient(nutritionData.protein), unit: 'g', color: 'bg-red-500', target: 80 },
                        { name: 'Carbs', value: formatMacronutrient(nutritionData.carbs), unit: 'g', color: 'bg-yellow-500', target: 100 },
                        { name: 'Fat', value: formatMacronutrient(nutritionData.fat), unit: 'g', color: 'bg-purple-500', target: 50 }
                      ].map((macro) => (
                        <div key={macro.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${macro.color}`}></div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{macro.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-16 md:w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                              <motion.div
                                className={`h-1.5 rounded-full ${macro.color}`}
                                initial={{ width: "0%" }}
                                animate={{ width: `${Math.min((macro.value / macro.target) * 100, 100)}%` }}
                                transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-900 dark:text-white w-10 text-right">
                              {macro.value}{macro.unit}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Other Nutrients */}
                  <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Other Nutrients</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { name: 'Fiber', value: formatMacronutrient(nutritionData.fiber), unit: 'g', icon: '🌾' },
                        { name: 'Sugar', value: formatMacronutrient(nutritionData.sugar), unit: 'g', icon: '🍯' },
                        { name: 'Sodium', value: Math.round(nutritionData.sodium), unit: 'mg', icon: '🧂' }
                      ].map((nutrient) => (
                                                <div key={nutrient.name} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-600/50 rounded-lg">
                          {/* Left: Emoji - smaller and consistent */}
                          <div className="text-base flex-shrink-0">{nutrient.icon}</div>
                          
                          {/* Right: Value on top, Label below - more compact */}
                          <div className="flex-1 min-w-0 text-center md:text-left">
                            <div className="font-bold text-gray-800 dark:text-white text-sm">
                              {nutrient.value}{nutrient.unit}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-300">{nutrient.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile-only: Ingredients */}
                  {isMobile && nutritionData.ingredients.length > 0 && (
                    <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Detected Ingredients</h3>
                      <div className="flex flex-wrap gap-2">
                        {nutritionData.ingredients.map((ingredient, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Suggestions */}
                  {nutritionData.ai_suggestions && nutritionData.ai_suggestions.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-700 p-4">
                      <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-3 flex items-center gap-2">
                        <Target size={20} />
                        AI Insights
                      </h3>
                      <div className="space-y-2">
                        {nutritionData.ai_suggestions.map((suggestion, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-purple-800 dark:text-purple-200">{suggestion}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-600 dark:text-gray-300">No nutrition data available</p>
              </div>
            )}
          </div>

          {/* Footer Status - Show auto-save status */}
          {!isLoading && nutritionData && !isMobile && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                {isSaving ? (
                  <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Saving meal to tracker...</span>
                  </div>
                ) : isSaved ? (
                  <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                    <Check size={20} />
                    <span className="font-medium">✅ Meal saved and logged to nutrition tracking!</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Analyzing meal...</span>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="ml-4"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalNutritionAnalysisModal; 